/**
 * REFERENCE-LINES (Homepal-only feature).
 *
 * This file owns the popover that lets Homepal staff configure two reference
 * lines on the metric chart. The lines themselves are driven by URL query
 * params (line_1, line_1_label, line_2, line_2_label), but every part of that
 * pipeline was built specifically for this feature — none of it is generic
 * infrastructure other features rely on.
 *
 * If we decide to drop reference lines entirely, grep for `REFERENCE-LINES`
 * and remove all of the following:
 *
 *   UI (this popover):
 *     - frontend/src/components_new/organisms/MetricView/ReferenceLinesMenu.tsx
 *       (this file)
 *     - In MetricView/index.tsx: the `ReferenceLinesMenu` import, the
 *       `useAccountSettings`/`isHomepal` hook, and the `<ReferenceLinesMenu />`
 *       render block.
 *
 *   Query params:
 *     - In types/GlobalMetric.tsx: `line_1`, `line_1_label`, `line_2`,
 *       `line_2_label` on `TMetricQueryParams`.
 *     - In utils/useMetricQueryParams.ts: PARAM_KEYS entries, the `parseLine`
 *       helper, the read block, and the write block for those four keys.
 *
 *   Chart plugin (keep drawHorizontalLines and `horizontalLines?`: the
 *   analysis trend and scatter use them too):
 *     - In molecules/charts/BarChart/index.tsx and LineChart/index.tsx:
 *       `drawHorizontalLines` plugin import + `plugins.drawHorizontalLines`
 *       options + plugin array registration.
 *
 *   Chart data builder:
 *     - In MetricView/index.tsx: the `horizontalLines` array builder and the
 *       `horizontalLines` prop passed to BarChart/LineChart.
 */

import React, { useState } from 'react'

import { TMetricQueryParams } from 'types/GlobalMetric'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Menu from 'components_new/atoms/Menu'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

interface ReferenceLinesMenuProps {
  queryParams: TMetricQueryParams
  resolvedUnit: string | null
  setQueryParams: (params: TMetricQueryParams) => void
}

const LINE_FIELDS = [
  {
    labelKey: 'line_1_label' as const,
    n: 1,
    valueKey: 'line_1' as const
  },
  {
    labelKey: 'line_2_label' as const,
    n: 2,
    valueKey: 'line_2' as const
  }
]

const ReferenceLinesMenu = (props: ReferenceLinesMenuProps) => {
  const { queryParams, resolvedUnit, setQueryParams } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const setLineValue = (key: 'line_1' | 'line_2', raw: string) => {
    const trimmed = raw.trim()

    if (trimmed === '') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _omit, ...rest } = queryParams

      setQueryParams(rest)

      return
    }

    const parsed = Number(trimmed)

    if (!Number.isFinite(parsed)) return

    setQueryParams({ ...queryParams, [key]: parsed })
  }

  const setLineLabel = (key: 'line_1_label' | 'line_2_label', raw: string) => {
    if (raw === '') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _omit, ...rest } = queryParams

      setQueryParams(rest)

      return
    }

    setQueryParams({ ...queryParams, [key]: raw })
  }

  return (
    <>
      <Tooltip title="Referenslinjer (endast Homepal)">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
        >
          <Icon fontSize="small" name="Straighten" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            width: 260
          }}
        >
          <Text variant="subtitle2">Referenslinjer</Text>
          {LINE_FIELDS.map(({ labelKey, n, valueKey }) => (
            <Box
              key={valueKey}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Text color="text.secondary" variant="caption">
                {`Linje ${n}`}
              </Text>
              <TextField
                fullWidth={true}
                label="Etikett"
                noGutter={true}
                onChange={(e) => setLineLabel(labelKey, e.target.value)}
                size="small"
                value={queryParams[labelKey] ?? ''}
              />
              <TextField
                InputProps={{
                  endAdornment: resolvedUnit ? (
                    <Text color="text.secondary" variant="body2">
                      {resolvedUnit}
                    </Text>
                  ) : undefined
                }}
                fullWidth={true}
                label="Värde"
                noGutter={true}
                onChange={(e) => setLineValue(valueKey, e.target.value)}
                size="small"
                type="number"
                value={queryParams[valueKey] ?? ''}
              />
            </Box>
          ))}
        </Box>
      </Menu>
    </>
  )
}

export default ReferenceLinesMenu
